import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { get, put, post } from '@/lib/api'

import { issueKeys } from './use-issues'

import type { Issue } from './use-issues'
import type { DeveloperGroup } from '@company/shared'

// ==================== Types ====================

export type IssueAssignmentRole = 'owner' | 'participant'

export interface IssueAssignmentDetail {
  id: string
  employeeId: string
  employeeName: string
  role: IssueAssignmentRole
  assignedBy: string
  assignedAt: string
}

export interface IssueAssignmentInput {
  ownerId?: string | null
  participantIds?: string[]
  developerGroup?: DeveloperGroup
}

export interface BulkAssignInput {
  issueIds: string[]
  ownerId?: string | null
  addParticipantIds?: string[]
  removeParticipantIds?: string[]
  developerGroup?: DeveloperGroup
}

export interface BulkAssignResult {
  success: boolean
  successCount: number
  failureCount: number
  failures: Array<{
    issueId: string
    issueCode: string
    error: string
  }>
}

// ==================== Query Keys ====================

export const bulkAssignmentKeys = {
  all: ['assignments'] as const,
  lists: () => [...bulkAssignmentKeys.all, 'list'] as const,
  list: (issueId: string) => [...bulkAssignmentKeys.lists(), issueId] as const,
}

// ==================== Hooks ====================

/**
 * 获取任务分配
 */
export function useIssueAssignmentsList(issueId: string) {
  return useQuery({
    queryKey: bulkAssignmentKeys.list(issueId),
    queryFn: () => get<IssueAssignmentDetail[]>(`/issues/${issueId}/assignments`),
    enabled: !!issueId,
  })
}

/**
 * 更新任务分配
 */
export function useUpdateAssignments() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ issueId, data }: { issueId: string; data: IssueAssignmentInput }) =>
      put<Issue>(`/issues/${issueId}/assignments`, data),
    onSuccess: (issue) => {
      void queryClient.invalidateQueries({ queryKey: bulkAssignmentKeys.list(issue.id) })
      void queryClient.invalidateQueries({ queryKey: issueKeys.detail(issue.id) })
      void queryClient.invalidateQueries({ queryKey: issueKeys.lists() })
      if (issue.projectId) {
        void queryClient.invalidateQueries({ queryKey: ['issues', 'kanban', issue.projectId] })
      }
    },
  })
}

/**
 * 批量分配任务
 */
export function useBulkAssign() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: BulkAssignInput) => post<BulkAssignResult>('/issues/batch-assign', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: bulkAssignmentKeys.all })
      void queryClient.invalidateQueries({ queryKey: issueKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: ['issues', 'kanban'] })
    },
  })
}
